import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../axios";
import { FiArrowLeft, FiUser, FiCheckCircle, FiCheck, FiShield } from "react-icons/fi";
import {
  FaBuilding,
  FaRegBuilding,
  FaRegDotCircle,
  FaRegUserCircle,
  FaRegCreditCard,
  FaAngleLeft,
  FaUniversity,
} from "react-icons/fa";
import { errorToast, successToast } from "../../../utils/toast";
import { CheckCircle, UploadCloud } from "lucide-react";
const KycStatus = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bankForm, setBankForm] = useState({
    fullName: "",
    number: "",
    ifsc: "",
    bankName: "",
    upiId: "",
  });
  const [personalForm, setPersonalForm] = useState({
    email: "",
    mobile: "",
  });
  const [focused, setFocused] = useState({
    fullName: false,
    number: false,
    ifsc: false,
    bankName: false,
    upiId: false,
    aadharNumber: false,
  });
  const [aadharNumber, setAadharNumber] = useState("");
  const [aadharPreview, setAadharPreview] = useState({
    front: null,
    back: null,
  });
  const [aadharFiles, setAadharFiles] = useState({
    front: null,
    back: null,
  });
  const [loading, setLoading] = useState(true);
  const [hasExistingKyc, setHasExistingKyc] = useState(false);
  const [selectedIdentity, setSelectedIdentity] = useState("PAN CARD");
  const [paymentMethod, setPaymentMethod] = useState("bank");
// bank | upi

  const stepLabels = ["", "", ""];

  useEffect(() => {
    const fetchKycData = async () => {
      try {
        const [kycResponse, profileResponse] = await Promise.allSettled([
          axios.get("getKyc"),
          axios.get("me"),
        ]);
        const kycData =
          kycResponse.status === "fulfilled"
            ? kycResponse.value?.data?.result || null
            : null;
        const profile =
          profileResponse.status === "fulfilled"
            ? profileResponse.value?.data?.result || null
            : null;

        setHasExistingKyc(Boolean(kycData));
        setBankForm({
          fullName: kycData?.holderName || profile?.name || "",
          number: kycData?.accountNo || "",
          ifsc: kycData?.ifsc || "",
          bankName: kycData?.bankName || "",
          upiId: kycData?.upiId || "",
        });
        setPersonalForm({
          email: kycData?.email || profile?.email || "",
          mobile: kycData?.mobile || profile?.mobile || "",
        });
        setAadharNumber(kycData?.aadharNo || "");
      } catch (error) {
        console.error("Failed to load KYC data:", error);
        setHasExistingKyc(false);
        // Fallback to /me endpoint if getKyc fails
        try {
          const { data } = await axios.get("me");
          const profile = data?.result || null;
          setBankForm({
            fullName: profile?.name || "",
            number: "",
            ifsc: profile?.bank?.ifsc || "",
            bankName: profile?.bank?.bankName || "",
            upiId: "",
          });
          setPersonalForm({
            email: profile?.email || "",
            mobile: profile?.mobile || "",
          });
        } catch (fallbackError) {
          console.error("Failed to load profile:", fallbackError);
          errorToast(
            fallbackError?.response?.data?.message ||
              "Failed to load profile details",
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchKycData();
  }, []);

const handleFileChange = (side, file) => {

  // FILE SIZE VALIDATION
  if (file && file.size > 2 * 1024 * 1024) {

    errorToast("Image size must be within 2 MB only");

    setAadharFiles((prev) => ({
      ...prev,
      [side]: null,
    }));

    setAadharPreview((prev) => ({
      ...prev,
      [side]: null,
    }));

    return;
  }

  if (file) {
    const reader = new FileReader();

    reader.onloadend = () => {
      setAadharPreview((prev) => ({
        ...prev,
        [side]: reader.result,
      }));
    };

    reader.readAsDataURL(file);
  }

  setAadharFiles((prev) => ({
    ...prev,
    [side]: file,
  }));
};

  const handleSubmitKyc = async () => {
    try {
      if (!validateStep1() || !validateStep2()) {
  return;
}
      const userId = localStorage.getItem("userId");
      const formData = new FormData();
      if (userId) {
        formData.append("userId", userId);
      }
      formData.append("holderName", bankForm.fullName);
      formData.append("accountNo", bankForm.number);
      formData.append("ifsc", bankForm.ifsc);
      formData.append("bankName", bankForm.bankName);
      formData.append("email", personalForm.email);
      formData.append("mobile", personalForm.mobile);
      formData.append("aadharNo", aadharNumber);
      formData.append("upiId", bankForm.upiId);

      if (aadharFiles.front) {
        formData.append("documentFrontImage", aadharFiles.front);
      }
      if (aadharFiles.back) {
        formData.append("documentBackImage", aadharFiles.back);
      }

      const kycEndpoint = hasExistingKyc ? "updateKyc" : "addKyc";
      const kycRequest = hasExistingKyc ? axios.put : axios.post;
      const response = await kycRequest(kycEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

     if (response.data?.result || response.data?.success) {

  successToast(
    hasExistingKyc
      ? "KYC updated successfully"
      : "KYC submitted successfully"
  );

  setTimeout(() => {
    navigate(-1);
  }, 1500);
}
   } catch (error) {

  console.log("KYC ERROR :", error);

  errorToast(
    error?.response?.data?.message ||
    error?.message ||
    "Failed to submit KYC"
  );
}
  };

  const handleInputChange = (field, value) => {
    setBankForm((prev) => ({ ...prev, [field]: value }));
  };

  const identityConfig = {
  "PAN CARD": {
    title: "PAN Number",
    placeholder: "Enter PAN Number",
    frontLabel: "PAN Front",
    backLabel: "PAN Back",
  },

  "VOTER ID": {
    title: "Voter ID Number",
    placeholder: "Enter Voter ID Number",
    frontLabel: "Front Side",
    backLabel: "Back Side",
  },

  "DRIVING LICENCE": {
    title: "Driving Licence Number",
    placeholder: "Enter Driving Licence Number",
    frontLabel: "Licence Front",
    backLabel: "Licence Back",
  },

  "ADHAAR CARD": {
    title: "Aadhaar Number",
    placeholder: "Enter Aadhaar Number",
    frontLabel: "Front Side",
    backLabel: "Back Side",
  },
};

const currentIdentity = identityConfig[selectedIdentity];

//step 1 VALIDATIONS
const validateStep1 = () => {
  if (paymentMethod === "bank") {

    if (!bankForm.fullName.trim()) {
      errorToast("Please enter full name");
      return false;
    }

    if (!bankForm.number.trim()) {
      errorToast("Please enter account number");
      return false;
    }

    if (!bankForm.ifsc.trim()) {
      errorToast("Please enter IFSC code");
      return false;
    }

    if (!bankForm.bankName.trim()) {
      errorToast("Please enter bank name");
      return false;
    }

  } else {

    if (!bankForm.upiId.trim()) {
      errorToast("Please enter UPI ID");
      return false;
    }

  }

  return true;
};

// Step 2 validations
const validateStep2 = () => {
  if (!aadharNumber.trim()) {
    errorToast(`Please enter ${currentIdentity.title}`);
    return false;
  }

  if (!aadharFiles.front) {
    errorToast("Please upload front image");
    return false;
  }

  if (
    selectedIdentity === "ADHAAR CARD" &&
    !aadharFiles.back
  ) {
    errorToast("Please upload back image");
    return false;
  }

  return true;
};

  const renderStepContent = () => {
   if (step === 1) {
  return (
    <div className="space-y-4">

      {/* REVIEW BOX */}
      {hasExistingKyc && (
        <div className="rounded-[24px] border border-[#E0B24D] bg-[#FFF4DD] p-3 shadow-sm">
          <h2 className="text-xl font-bold text-[#1C132E]">
            KYC Under Review
          </h2>

          <p className="mt-2 text-sm leading-7 text-left text-[#4A415B]">
            Your KYC has been submitted and is waiting for approval.
          </p>
        </div>
      )}

      {/* HEADING */}
      <div>
        <h1 className="text-xl text-left leading-none font-bold text-[#140D26]">
          Bank Details
        </h1>
      </div>
 <div className="grid grid-cols-2 gap-4 mb-6">

  {/* Bank */}
  <button
    type="button"
    onClick={() => setPaymentMethod("bank")}
    className={`relative flex items-center gap-4 rounded-3xl border p-4 transition-all ${
      paymentMethod === "bank"
        ? "border-[#7B2CBF] bg-[#F8F1FD]"
        : "border-gray-200 bg-white"
    }`}
  >
    {/* Radio */}
    <div className="absolute right-3 top-1">
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-full border ${
          paymentMethod === "bank"
            ? "border-[#7B2CBF] bg-[#7B2CBF]"
            : "border-gray-300"
        }`}
      >
        {paymentMethod === "bank" && (
          <FiCheck className="text-sm text-white" />
        )}
      </div>
    </div>

    {/* Icon */}
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F1E4FB]">
      <FaUniversity className="text-3xl text-[#7B2CBF]" />
    </div>

    {/* Text */}
    <div className="text-left mt-2">
      <h3 className="font-bold text-[#32104D]">Bank Account</h3>
      <p className="text-sm text-gray-500">
        Receive payment in your bank account
      </p>
    </div>
  </button>

  {/* UPI */}
  <button
    type="button"
    onClick={() => setPaymentMethod("upi")}
    className={`relative flex items-center gap-4 rounded-3xl border p-4 transition-all ${
      paymentMethod === "upi"
        ? "border-[#7B2CBF] bg-[#F8F1FD]"
        : "border-gray-200 bg-white"
    }`}
  >
    {/* Radio */}
    <div className="absolute right-3 top-1">
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-full border ${
          paymentMethod === "upi"
            ? "border-[#7B2CBF] bg-[#7B2CBF]"
            : "border-gray-300"
        }`}
      >
        {paymentMethod === "upi" && (
          <FiCheck className="text-sm text-white" />
        )}
      </div>
    </div>

    {/* Icon */}
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F5F5F5]">
      <FaRegCreditCard  className="h-8 object-contain" />
    </div>

    {/* Text */}
    <div className="text-left mt-2">
      <h3 className="font-bold text-[#32104D]">UPI ID</h3>
      <p className="text-sm text-gray-500">
        Receive payment via UPI
      </p>
    </div>
  </button>

</div>

      {/* FORM */}
      {paymentMethod === "bank" && (
      <div className="space-y-3">

        {[
          {
            label: "Enter Full Name",
            value: bankForm.fullName,
            field: "fullName",
            icon: <FiUser className="h-6 w-6" />,
            type: "text",
          },
          {
            label: "Enter Account Number",
            value: bankForm.number,
            field: "number",
            icon: <FaRegBuilding className="h-6 w-6" />,
            type: "number",
          },
          {
            label: "IFSC Code",
            value: bankForm.ifsc,
            field: "ifsc",
            icon: <FaRegCreditCard className="h-6 w-6" />,
            type: "text",
          },
          {
            label: "Bank Name",
            value: bankForm.bankName,
            field: "bankName",
            icon: <FaBuilding className="h-6 w-6" />,
            type: "text",
          },
        ].map((input) => (
          <div
            key={input.field}
            className="relative rounded-[24px] border border-[#D9D9E3] bg-[#F7F5FB] px-3 py-2"
          >

            {/* ICON */}
            <div className="absolute left-5 top-1/2 -translate-y-1/2 text-[#3A3345]">
              {input.icon}
            </div>

            {/* LABEL */}
            <label
              className={`absolute left-16 transition-all duration-200 ${
                focused[input.field] || input.value
                  ? "top-3 text-sm text-[#5B2B74]"
                  : "top-1/2 -translate-y-1/2 text-base text-[#8D8A97]"
              }`}
            >
              {input.label}
            </label>

            {/* INPUT */}
           <input
  type={input.type}
  value={input.value}
  onChange={(e) =>
    handleInputChange(
      input.field,
      input.field === "ifsc"
        ? e.target.value.toUpperCase()
        : e.target.value
    )
  }
  onFocus={() =>
    setFocused((prev) => ({
      ...prev,
      [input.field]: true,
    }))
  }
  onBlur={() =>
    setFocused((prev) => ({
      ...prev,
      [input.field]: input.value !== "",
    }))
  }
  className="
    w-full
    bg-transparent
    pt-6
    pl-11
    text-[17px]
    font-medium
    text-[#222]
    border-none
    outline-none
    focus:outline-none
    focus:ring-0
    focus:border-none
    focus:shadow-none
  "
/>

          </div>
          
        ))}
      </div>
      
      )}

      {paymentMethod === "upi" && (
  <div className="space-y-3">

    <div className="relative rounded-[24px] border border-[#D9D9E3] bg-[#F7F5FB] px-3 py-2">

      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-[#3A3345]">
        <FaRegCreditCard className="h-6 w-6" />
      </div>

      <label
        className={`absolute left-16 transition-all duration-200 ${
          focused.upiId || bankForm.upiId
            ? "top-3 text-sm text-[#5B2B74]"
            : "top-1/2 -translate-y-1/2 text-base text-[#8D8A97]"
        }`}
      >
        Enter UPI ID
      </label>

      <input
        type="text"
        value={bankForm.upiId}
        onChange={(e) => handleInputChange("upiId", e.target.value)}
        onFocus={() =>
          setFocused((prev) => ({ ...prev, upiId: true }))
        }
        onBlur={() =>
          setFocused((prev) => ({
            ...prev,
            upiId: bankForm.upiId !== "",
          }))
        }
        className="w-full bg-transparent pt-6 pl-11 text-[17px] outline-none"
      />
    </div>

  </div>
)}
<div className="mt-6 rounded-3xl border bg-[#FBF6FD] p-4 flex items-center gap-4">

    <div className="h-12 w-12 rounded-full bg-[#F1E4FB] flex items-center justify-center">
        <FiShield className="text-[#7B2CBF] text-2xl"/>
    </div>

    <div className="flex-1">
        <h4 className="font-bold">
            Your information is 100% secure
        </h4>

        <p className="text-[10px] text-gray-500">
            We use bank-level security to protect your data
        </p>
    </div>

    <FiCheckCircle className="text-[#7B2CBF] text-xl"/>
</div>

     {/* // CONTINUE BUTTON */}
<button
            type="button"
           onClick={() => {
  if (validateStep1()) {
    setStep(2);
  }
}}
            className="mx-auto block w-full max-w-md rounded-full bg-[#F9A826] px-6 py-4 text-base font-semibold text-white shadow-xl transition hover:opacity-95"
          >
            Continue
          </button>
    </div>
  );
}

  if (step === 2) {
  return (
    <div className="space-y-3 pb-20">
        {/* REVIEW BOX */}
      {hasExistingKyc && (
        <div className="rounded-[24px] border border-[#E0B24D] bg-[#FFF4DD] p-3 shadow-sm">
          <h2 className="text-xl font-bold text-[#1C132E]">
            KYC Under Review
          </h2>

          <p className="mt-2 text-sm leading-7 text-left text-[#4A415B]">
            Your KYC has been submitted and is waiting for approval.
          </p>
        </div>
      )}
      {/* HEADING */}
      <div>
        <h1 className="text-2xl font-extrabold text-[#120C23]">
          Identity Details
        </h1>
      </div>

      {/* CARD */}
      <div className="rounded-[28px] border border-[#D8CCE4] bg-[#EDE6F3] p-3 items-start justify-between">

        {/* Identity Proof */}
        <h2 className="text-lg font-semibold text-left text-[#1B1230]">
          Identity Proof
        </h2>

        <div className="mt-2 space-y-3">

          {[
  "PAN CARD",
  "VOTER ID",
  "DRIVING LICENCE",
  "ADHAAR CARD",
].map((item, index) => (
  <label
    key={index}
    className="flex items-center gap-2 cursor-pointer"
  >
    <input
      type="radio"
      name="identity"
      checked={selectedIdentity === item}
      onChange={() => setSelectedIdentity(item)}
      className="h-5 w-5 text-bgBlue"
    />

    <span className="text-[16px] font-medium text-[#3A3345]">
      {item}
    </span>
  </label>
))}
        </div>

        {/* Aadhaar */}
        <div className="mt-5">
          <h2 className="text-xl text-left font-extrabold text-[#120C23]">
  {currentIdentity.title}
</h2>

          <div className="mt-5 rounded-[22px] bg-[#F5F5F5] px-4 py-2">
            <input
              type="text"
              placeholder={currentIdentity.placeholder}
              value={aadharNumber}
              onChange={(e) => setAadharNumber(e.target.value)}
              className="w-full bg-transparent text-xl text-black focus-none outline-none border border-none placeholder:text-gray-400"
            />
          </div>
        </div>

      
       {/* Upload Boxes */}
<div
  className={`mt-4 grid gap-3 ${
    selectedIdentity === "ADHAAR CARD"
      ? "grid-cols-2"
      : "grid-cols-1"
  }`}
>

  {/* FRONT */}
  <div
    className={`rounded-[26px] border-2 border-dashed p-3 text-center relative overflow-hidden transition-all
    ${
      aadharPreview.front
        ? "border-[#2AA84A] bg-[#EDF8F0]"
        : "border-[#9A76B2] bg-[#F3ECFA]"
    }`}
  >
    {/* Uploaded Image */}
    {aadharPreview.front && (
      <img
        src={aadharPreview.front}
        alt="Front"
        className="w-full h-32 rounded-2xl object-cover"
      />
    )}

    <input
      type="file"
      accept="image/*"
      onChange={(e) =>
        handleFileChange("front", e.target.files[0])
      }
      className="hidden"
      id="front-upload"
    />

    <label
      htmlFor="front-upload"
      className="cursor-pointer flex flex-col items-center"
    >
      {/* NO IMAGE */}
      {!aadharPreview.front ? (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#E4D6F2]">
            <svg
              className="h-10 w-10 text-[#6C3A83]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
              />
            </svg>
          </div>

          <h3 className="mt-4 text-xl font-bold text-[#4B2D83]">
            {currentIdentity.frontLabel}
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            ID Photo
          </p>

          <p className="flex items-center gap-2 mt-5 text-sm font-medium text-[#8A6BA5]">
            <UploadCloud size={18} />
            Upload Image
          </p>
        </>
      ) : (
        <>
          {/* IMAGE UPLOADED UI */}
          <h3 className="mt-4 text-xl font-bold text-[#2AA84A]">
            {currentIdentity.frontLabel}
          </h3>

          <p className="mt-1 text-sm text-gray-600">
            Tap to replace image
          </p>

          <p className="flex items-center gap-2 mt-4 text-sm font-semibold text-[#2AA84A]">
            <CheckCircle size={18} />
            Image Added
          </p>
        </>
      )}
    </label>
  </div>

  {/* BACK */}
  {selectedIdentity === "ADHAAR CARD" && (
  <div
    className={`rounded-[26px] border-2 border-dashed `}
  >
    <div
    className={`rounded-[26px] border-2 border-dashed p-3 text-center relative overflow-hidden transition-all
    ${
      aadharPreview.back
        ? "border-[#2AA84A] bg-[#EDF8F0]"
        : "border-[#9A76B2] bg-[#F3ECFA]"
    }`}
  >
    {/* Uploaded Image */}
    {aadharPreview.back && (
      <img
        src={aadharPreview.back}
        alt="Back"
        className="w-full h-32 rounded-2xl object-cover"
      />
    )}

    <input
      type="file"
      accept="image/*"
      onChange={(e) =>
        handleFileChange("back", e.target.files[0])
      }
      className="hidden"
      id="back-upload"
    />

    <label
      htmlFor="back-upload"
      className="cursor-pointer flex flex-col items-center"
    >
      {/* NO IMAGE */}
      {!aadharPreview.back ? (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#E4D6F2]">
            <svg
              className="h-10 w-10 text-[#6C3A83]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
              />
            </svg>
          </div>

          <h3 className="mt-4 text-xl font-bold text-[#4B2D83]">
            {currentIdentity.backLabel}
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            Address Info
          </p>

          <p className="flex items-center gap-2 mt-5 text-sm font-medium text-[#8A6BA5]">
            <UploadCloud size={18} />
            Upload Image
          </p>
        </>
      ) : (
        <>
          {/* IMAGE UPLOADED UI */}
          <h3 className="mt-4 text-xl font-bold text-[#2AA84A]">
            {currentIdentity.backLabel}
          </h3>

          <p className="mt-1 text-sm text-gray-600">
            Tap to replace image
          </p>

          <p className="flex items-center gap-2 mt-4 text-sm font-semibold text-[#2AA84A]">
            <CheckCircle size={18} />
            Image Added
          </p>
        </>
      )}
    </label>
  </div>
  </div>
)}
  
</div>
  
      </div>

      {/* BUTTON */}
      <button
        type="button"
      onClick={() => {
  if (validateStep2()) {
    setStep(3);
  }
}}
        className="w-full rounded-full bg-[#F4A911] py-4 text-xl font-medium text-white shadow-xl"
      >
        Continue
      </button>
    </div>
  );
}

    return (
      <div className="space-y-3">
        <div className="rounded-[20px] text-left border border-[#D4B45A] bg-[#FFF6E1] p-6 shadow-sm">
          <h2 className="text-xl font-bold text-[#291A4B]">KYC Under Review</h2>
          <p className="mt-1 text-sm leading-6 text-[#4E4077]">
            Your KYC has been submitted and is waiting for approval.
          </p>
        </div>
        <div className="rounded-[30px] p-5">
          <h2 className="text-3xl text-left font-bold text-[#191919]">
            Review
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                title: "Identity Details",
                subtitle: aadharNumber
                  ? `Aadhaar: ${aadharNumber.slice(-4)}`
                  : "Aadhaar Card",
                icon: <FaRegCreditCard className="h-5 w-5 text-[#4B1C72]" />,
              },
              {
                title: "Bank Details",
                subtitle: bankForm.upiId || bankForm.bankName || "Bank details",
                icon: <FaBuilding className="h-5 w-5 text-[#4B1C72]" />,
              },
              {
                title: "Personal Information",
                subtitle: bankForm.fullName || "Personal details",
                icon: <FaRegUserCircle className="h-5 w-5 text-[#4B1C72]" />,
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-center justify-between rounded-[28px] border border-[#1E9D57] bg-[#F7F6FB] px-4 py-4"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E6E0F6] text-[#4B1C72]">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#191919]">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm text-[#6B6B78]">
                      {item.subtitle}
                    </p>
                  </div>
                </div>
                <FiCheckCircle className="h-6 w-6 text-[#1E9D57]" />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmitKyc}
          className="mx-auto block w-full max-w-md rounded-full bg-[#F9A826] px-6 py-4 text-base font-semibold text-white shadow-xl transition hover:opacity-95"
        >
          Submit
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F3F8] flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#4B1D67] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F3F8] lg:mt-10 px-3 py-3 text-slate-900 sm:px-4 overflow-hidden">
      {" "}
      <div className="mx-auto max-w-3xl">
        <div className="mb-2 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/60"
          >
            <FaAngleLeft className="h-5 w-5 text-[#191919]" />
          </button>
          <h1 className="text-xl font-bold">KYC Details</h1>
        </div>

        <div className="mb-3 flex items-center gap-3">
          {stepLabels.map((label, index) => {
            const isActive = step === index + 1;
            const isCompleted = step > index + 1;
            return (
              <div key={label} className="flex items-center gap-3">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold ${
                    isActive || isCompleted
                      ? "bg-[#4B1D67] text-white border-[#4B1D67]"
                      : "border-gray-300 text-gray-500 "
                  }`}
                >
                  {index + 1}
                </div>
                <span
                  className={`text-sm ${isActive ? "text-[#4B1D67]" : "text-gray-500"}`}
                >
                  {label}
                </span>
                {index < stepLabels.length - 1 && (
                  <div className="h-px w-10 bg-gray-300" />
                )}
              </div>
            );
          })}
        </div>

        {renderStepContent()}
      </div>
    </div>
  );
};

export default KycStatus;
